import { filter, find, isEqual, map } from 'lodash-es';

/**
 * Native port of the v1 goals factories (account-v1/src/components/goals/):
 * `goalType` (constants/goalType.js), `goalDescriptor` (factories/goalDescriptor.js)
 * and `updateGoalsSerializer` (factories/goalsSerializer.js). WS5 #10 Stage A inc 2s.
 * Pure logic, ported VERBATIM (incl. the loose `==` type checks and the
 * parseInt(...,10) NaN flows).
 */
export const goalType = {
  SpreadTheWord: 0,
  GetSocialConnections: 1,
  GetUniqueVisitors: 2,
  SignupAndGet: 3,
  SubmitQuoteAndGet: 4,
  ShareAndGet: 5,
  SubmitPhotoAndGet: 6,
  PhotoContestWinner: 7,
  AttendeeSignup: 8,
} as const;

/** v1 `goalDescriptor(goal)` — maps a goal to its `{type[, value]}` descriptor. */
export function goalDescriptor(goal: any): any {
  return (
    getAdvocateDescriptor(goal) ||
    getSocialConnectionsDescriptor(goal) ||
    getSharingDescriptor(goal) ||
    getParticipationDescriptor(goal) ||
    getSubmitPhotoDescriptor(goal) ||
    getPhotoContestDescriptor(goal) ||
    getGroupAdvocacyAttendeeSignupDescriptor(goal)
  );
}

function getAdvocateDescriptor(goal: any): any {
  if (goal.type == 1) {
    const descriptor: any = { type: goalType.SpreadTheWord };
    const clicks = parseInt(goal.clicks, 10);
    const signups = parseInt(goal.signups, 10);
    if (clicks > 0) {
      descriptor.type = goalType.GetUniqueVisitors;
      descriptor.value = clicks;
    } else if (signups > 0) {
      descriptor.type = goalType.GetSocialConnections;
      descriptor.value = signups;
    }
    return descriptor;
  }
}

function getSocialConnectionsDescriptor(goal: any): any {
  if (goal.type == 2) return { type: goalType.SignupAndGet };
}
function getSharingDescriptor(goal: any): any {
  if (goal.type == 4) return { type: goalType.ShareAndGet };
}
function getParticipationDescriptor(goal: any): any {
  if (goal.type == 3) return { type: goalType.SubmitQuoteAndGet };
}
function getSubmitPhotoDescriptor(goal: any): any {
  if (goal.type == 6) return { type: goalType.SubmitPhotoAndGet };
}
function getPhotoContestDescriptor(goal: any): any {
  if (goal.type == 5) return { type: goalType.PhotoContestWinner };
}
function getGroupAdvocacyAttendeeSignupDescriptor(goal: any): any {
  if (goal.type == 7) return { type: goalType.AttendeeSignup };
}

/** v1 `updateGoalsSerializer.createGoalModel(goal, type)`. */
export function createGoalModel(goal: any, type: any): any {
  const model: any = {
    incentiveUUID: goal.incentiveUUID,
    isActive: goal.isActive == 1,
    type,
  };
  if (goal.id) {
    model.uuid = goal.id;
  }
  addDataForAdvocates(model, goal);
  return model;
}

function addDataForAdvocates(model: any, goal: any): void {
  if (model.type == 1) {
    const descriptor = goalDescriptor(goal);
    if (descriptor.type === goalType.GetUniqueVisitors) {
      model['uniqueVisitors'] = descriptor.value;
    } else if (descriptor.type === goalType.GetSocialConnections) {
      model['signups'] = descriptor.value;
    } else {
      model['uniqueVisitors'] = 0;
      model['signups'] = 0;
    }
  }
}

/**
 * v1 `updateGoalsSerializer.serialize(model, originalGoals)` — the
 * newGoals/updateGoals/deleteGoals envelope. `angular.equals` → lodash
 * `isEqual` (both deep-equal plain data; the campaign goal models contain
 * only JSON primitives so the angular.equals function/$-key carve-outs are
 * immaterial here).
 */
export function serializeGoalsModel(model: any, originalGoals: any[]): any {
  return {
    newGoals: map(model.goals.newGoals, (goal: any) => createGoalModel(goal, model.type)),
    updateGoals: createUpdateGoalsArray(model, originalGoals),
    deleteGoals: model.goals.deleteGoals,
  };
}

function createUpdateGoalsArray(model: any, originalGoals: any[]): any[] {
  const goalsThatRequireUpdate = filter(model.goals.updateGoals, (goal: any) => {
    const originalGoal = createGoalModel(
      find(originalGoals, (g: any) => g.id == goal.id),
      model.type,
    );
    const currentGoal = createGoalModel(goal, model.type);
    return !isEqual(originalGoal, currentGoal);
  });
  return map(goalsThatRequireUpdate, (goal: any) => createGoalModel(goal, model.type));
}
